/**
 * useMenuManagement Hook
 * Manages menu items, drag-and-drop, and permission-based filtering
 */

import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  KanbanSquare,
  ChartGantt,
  Settings,
  FileText,
  FileBarChart,
  Calendar,
} from 'lucide-react';
import { MenuItem } from '@/shared/types';
import type { User } from '@/shared/apiTypes';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { canOpenWorkspaceView } from '@/shared/workspaceViewAccess';

interface MenuManagementOptions {
  user: User | null;
  hasPermission: (resource: string, action: string) => boolean;
  storageScopeKey: string;
}

const menuDefinitions: Array<Omit<MenuItem, 'label'> & { labelKey: string }> = [
  { id: '1', labelKey: 'Dashboard', icon: LayoutDashboard, view: 'dashboard' },
  { id: '3', labelKey: 'Gantt Chart', icon: ChartGantt, view: 'gantt' },
  { id: '2', labelKey: 'Kanban', icon: KanbanSquare, view: 'kanban' },
  { id: '7', labelKey: 'Calendar', icon: Calendar, view: 'calendar' },
  { id: '5', labelKey: 'Docs', icon: FileText, view: 'docs' },
  { id: '6', labelKey: 'Reports', icon: FileBarChart, view: 'reports' },
  { id: '4', labelKey: 'Settings', icon: Settings, view: 'settings' },
];

const MENU_ORDER_STORAGE_PREFIX = 'app-menu-order';

const getMenuOrderStorageKey = (storageScopeKey: string) =>
  `${MENU_ORDER_STORAGE_PREFIX}:${storageScopeKey || 'anonymous'}`;

const readMenuOrder = (storageScopeKey: string): string[] | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getMenuOrderStorageKey(storageScopeKey));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : null;
  } catch {
    return null;
  }
};

const writeMenuOrder = (storageScopeKey: string, menuItems: MenuItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getMenuOrderStorageKey(storageScopeKey),
      JSON.stringify(menuItems.map((item) => item.id)),
    );
  } catch {
    // Menu order persistence is optional; keep the visible menu usable.
  }
};

const applyMenuOrder = (items: MenuItem[], order: string[] | null): MenuItem[] => {
  if (!order?.length) return items;
  const orderedItems = order
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is MenuItem => Boolean(item));
  const orderedIds = new Set(orderedItems.map((item) => item.id));
  return [
    ...orderedItems,
    ...items.filter((item) => !orderedIds.has(item.id)),
  ];
};

const getPermittedMenuItems = (
  user: User | null,
  hasPermission: (resource: string, action: string) => boolean,
  translate: (key: string) => string,
): MenuItem[] => {
  if (!user) return [];

  return menuDefinitions
    .filter((item) => canOpenWorkspaceView(item.view, hasPermission))
    .map(({ labelKey, ...item }) => ({ ...item, label: translate(labelKey) }));
};

export function useMenuManagement({ user, hasPermission, storageScopeKey }: MenuManagementOptions) {
  const { t } = useSettings();
  // Initialize menu items from saved order
  const [menuItems, setMenuItems] = useState<MenuItem[]>(() => {
    return applyMenuOrder(
      getPermittedMenuItems(user, hasPermission, t),
      readMenuOrder(storageScopeKey),
    );
  });

  // Drag state
  const [draggedMenu, setDraggedMenu] = useState<{ menu: MenuItem; index: number } | null>(null);
  const [dragOverMenu, setDragOverMenu] = useState<number | null>(null);

  // Update menu items based on permissions
  useEffect(() => {
    if (!user) {
      setMenuItems([]);
      return;
    }

    const filteredItems = getPermittedMenuItems(user, hasPermission, t);
    setMenuItems((prev) => {
      // Preserve order if possible
      const savedOrder = readMenuOrder(storageScopeKey);
      const currentOrder = prev.map((item) => item.id);
      return applyMenuOrder(filteredItems, savedOrder || currentOrder);
    });
  }, [user, hasPermission, storageScopeKey, t]);

  // Drag handlers
  const handleMenuDragStart = (e: React.DragEvent, menu: MenuItem, index: number) => {
    e.dataTransfer.setData('application/x-dnd-menu-item', menu.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedMenu({ menu, index });
  };

  const handleMenuDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedMenu && draggedMenu.index !== index) {
      setDragOverMenu(index);
    }
  };

  const handleMenuDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('application/x-dnd-menu-item')) {
      return;
    }

    if (draggedMenu && draggedMenu.index !== index) {
      const newMenuItems = [...menuItems];
      const [removed] = newMenuItems.splice(draggedMenu.index, 1);
      newMenuItems.splice(index, 0, removed);
      setMenuItems(newMenuItems);

      writeMenuOrder(storageScopeKey, newMenuItems);
    }
    setDraggedMenu(null);
    setDragOverMenu(null);
  };

  const handleMenuDragEnd = () => {
    setDraggedMenu(null);
    setDragOverMenu(null);
  };

  // Keyboard alternative to drag reordering: move one tab left (-1) or right (+1) and persist it.
  const moveMenuItem = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (index < 0 || index >= menuItems.length || target < 0 || target >= menuItems.length) return;
    const newMenuItems = [...menuItems];
    const [moved] = newMenuItems.splice(index, 1);
    newMenuItems.splice(target, 0, moved);
    setMenuItems(newMenuItems);
    writeMenuOrder(storageScopeKey, newMenuItems);
  };

  return {
    menuItems,
    draggedMenu,
    dragOverMenu,
    handleMenuDragStart,
    handleMenuDragOver,
    handleMenuDrop,
    handleMenuDragEnd,
    moveMenuItem,
    setDragOverMenu,
  };
}
